/**
 * WorkingDaysManager - จัดการวันทำงานและการแปลงระหว่างวันที่และดัชนีใน Timeline
 * โดยใช้ระบบดัชนีที่สอดคล้องกับ Timeline headers โดยตรง
 */
class WorkingDaysManager {
  /**
   * สร้าง WorkingDaysManager ใหม่
   * @param {TimelineCalendar} timeline - อินสแตนซ์ของ TimelineCalendar
   */
  constructor(timeline) {
    this.timeline = timeline;
    this.dateToIndexMap = new Map();
    this.indexToDateMap = new Map();
    this.workingDays = {};
    this.firstDate = null;
    this.lastDate = null;
    this.minIndex = 0;
    this.maxIndex = 0;

    this.initialized = true;
  }

  /**
   * สร้างข้อมูลวันทำงานเริ่มต้น โดยกำหนดให้วันหยุดตาม timeline.weekEnds
   * @param {Date} startDate - วันที่เริ่มต้น
   * @param {Date} endDate - วันที่สิ้นสุด
   * @private
   */
  _generateDefaultWorkingDays(startDate, endDate) {
    const extendedStart = new Date(startDate);
    extendedStart.setMonth(extendedStart.getMonth() - 1);

    const extendedEnd = new Date(endDate);
    extendedEnd.setMonth(extendedEnd.getMonth() + 1);

    const weekEnds = this.timeline.options.weekEnds || [0, 6];

    this.workingDays = {};

    const showHolidays = this.timeline.options.showHolidays !== undefined ? this.timeline.options.showHolidays : true;

    let current = new Date(extendedStart);
    let currentIndex = 0;

    while (current <= extendedEnd) {
      const dateStr = this.timeline.formatDate(current, 'y-m-d', 0);
      const dayOfWeek = current.getDay();
      const isWeekend = weekEnds.includes(dayOfWeek);

      if (showHolidays || !isWeekend) {
        this.workingDays[dateStr] = currentIndex;
        currentIndex++;
      }

      current.setDate(current.getDate() + 1);
    }

    if (Object.keys(this.workingDays).length > 0) {
      const dates = Object.keys(this.workingDays).sort();
      this.firstDate = dates[0];
      this.lastDate = dates[dates.length - 1];
      this.minIndex = Math.min(...Object.values(this.workingDays));
      this.maxIndex = Math.max(...Object.values(this.workingDays));
    }
  }

  /**
   * รับข้อมูล workingDays จาก API
   * @param {Object} workingDays - ข้อมูล workingDays จาก API
   */
  setWorkingDays(workingDays, forceReset = false) {
    if (forceReset) {
      this.reset();
    }

    if (!workingDays || Object.keys(workingDays).length === 0) {
      if (this.timeline.currentProject &&
        this.timeline.currentProject.startDate &&
        this.timeline.currentProject.endDate) {
        this._generateDefaultWorkingDays(
          new Date(this.timeline.currentProject.startDate),
          new Date(this.timeline.currentProject.endDate)
        );
        this.buildMaps();
      }
      return;
    }

    this.workingDays = workingDays;

    const dates = Object.keys(workingDays).sort();
    this.firstDate = dates[0];
    this.lastDate = dates[dates.length - 1];
    this.minIndex = Math.min(...Object.values(workingDays));
    this.maxIndex = Math.max(...Object.values(workingDays));

    this.buildMaps();

    this.initialized = true;
  }

  /**
   * สร้างแมพสำหรับการแปลงวันที่เป็นดัชนีและย้อนกลับ
   */
  buildMaps() {
    if (!this.workingDays || Object.keys(this.workingDays).length === 0) {
      return;
    }

    this.dateToIndexMap.clear();
    this.indexToDateMap.clear();

    let referenceDate = null;
    if (this.timeline.options.filterRange && this.timeline.options.filterRange.start) {
      referenceDate = this.timeline.options.filterRange.start;
    }

    if (!referenceDate && Object.keys(this.workingDays).length > 0) {
      const dates = Object.keys(this.workingDays).sort();
      referenceDate = dates[0];
    }

    if (!referenceDate) {
      referenceDate = this.timeline.formatDate(new Date(), 'y-m-d', 0);
    }

    const sortedDates = Object.keys(this.workingDays).sort();

    let referenceIndex = sortedDates.indexOf(referenceDate);
    if (referenceIndex === -1) {
      for (const date of sortedDates) {
        if (date > referenceDate) {
          referenceDate = date;
          referenceIndex = sortedDates.indexOf(date);
          break;
        }
      }

      if (referenceIndex === -1 && sortedDates.length > 0) {
        referenceDate = sortedDates[0];
        referenceIndex = 0;
      }
    }

    const adjustedWorkingDays = {};
    for (const date of sortedDates) {
      const originalIndex = sortedDates.indexOf(date);
      const adjustedIndex = originalIndex - referenceIndex;
      adjustedWorkingDays[date] = adjustedIndex;
    }

    for (const [date, index] of Object.entries(adjustedWorkingDays)) {
      const numIndex = Number(index);
      this.dateToIndexMap.set(date, numIndex);
      this.indexToDateMap.set(numIndex, date);
    }

    if (this.dateToIndexMap.size > 0) {
      const indices = Array.from(this.dateToIndexMap.values());
      this.minIndex = Math.min(...indices);
      this.maxIndex = Math.max(...indices);
    }
  }

  /**
   * รีเซ็ตและเริ่มต้นใหม่เมื่อมีการเปลี่ยนแปลงข้อมูล
   */
  reset() {
    this.dateToIndexMap.clear();
    this.indexToDateMap.clear();
    this.workingDays = {};
    this.firstDate = null;
    this.lastDate = null;
    this.initialized = false;
    this.minIndex = 0;
    this.maxIndex = 0;
  }

  /**
   * แปลงวันที่เป็นดัชนีที่สัมพันธ์กับ headers ของ timeline
   * @param {string} dateStr - วันที่ในรูปแบบ YYYY-MM-DD
   * @returns {number} - ดัชนีของวันที่ (0 = วันแรกของ timeline)
   */
  dateToIndex(dateStr) {
    return this.dateToIndexMap.get(dateStr);
  }

  /**
   * แปลงดัชนีเป็นวันที่ ที่สัมพันธ์กับ headers ของ timeline
   * @param {number} index - ดัชนี (0 = วันแรกของ timeline)
   * @returns {string|null} - วันที่ในรูปแบบ YYYY-MM-DD หรือ null ถ้าไม่สามารถคำนวณได้
   */
  indexToDate(index) {
    return this.indexToDateMap.get(index);
  }

  /**
   * คำนวณวันที่สิ้นสุดจากวันที่เริ่มต้นและระยะเวลา
   * @param {string} startDate - วันที่เริ่มต้นในรูปแบบ YYYY-MM-DD
   * @param {number} duration - ระยะเวลาเป็นจำนวนวัน
   * @returns {string|null} - วันที่สิ้นสุด หรือ null ถ้าไม่สามารถคำนวณได้
   */
  calculateEndDate(startDate, duration) {
    if (duration < 1) {
      return null;
    }

    let endDate = null;
    for (const [date, index] of this.dateToIndexMap.entries()) {
      if (startDate <= date) {
        duration--;
        if (duration === 0) {
          endDate = date;
          break;
        }
      }
    }

    return endDate;
  }

  /**
   * คำนวณระยะเวลาระหว่างสองวันที่
   * @param {string} startDate - วันที่เริ่มต้นในรูปแบบ YYYY-MM-DD
   * @param {string} endDate - วันที่สิ้นสุดในรูปแบบ YYYY-MM-DD
   * @returns {number} - จำนวนวันระหว่างสองวันที่ หรือ 0 ถ้าไม่สามารถคำนวณได้
   */
  calculateDuration(startDate, endDate) {
    const startIndex = this.dateToIndex(startDate);
    const endIndex = this.dateToIndex(endDate);

    if (startIndex === -1 || endIndex === -1) return 0;
    if (endIndex < startIndex) return 0;

    return endIndex - startIndex + 1;
  }

  getFirstDate() {
    return this.markerDayToDate(0);
  }

  /**
   * คำนวณวันที่หลังจากเพิ่มจำนวนวัน
   * @param {string} dateStr - วันที่เริ่มต้นในรูปแบบ YYYY-MM-DD
   * @param {number} days - จำนวนวันที่ต้องการเพิ่ม
   * @returns {string|null} - วันที่ใหม่ หรือ null ถ้าไม่สามารถคำนวณได้
   */
  addDays(dateStr, days) {
    const startIndex = this.dateToIndex(dateStr);
    if (startIndex === -1) return null;

    const newIndex = startIndex + days;

    return this.indexToDate(newIndex);
  }

  /**
   * แปลงตำแหน่งของ Marker เป็นวันที่
   * @param {number} day - ตำแหน่งวันของ Marker (0-based)
   * @returns {string|null} - วันที่ในรูปแบบ YYYY-MM-DD หรือ null ถ้าไม่ถูกต้อง
   */
  markerDayToDate(day) {
    return this.indexToDate(day);
  }

  /**
   * แปลงวันที่เป็นตำแหน่งของ Marker
   * @param {string} dateStr - วันที่ในรูปแบบ YYYY-MM-DD
   * @returns {number} - ตำแหน่งวันของ Marker (0-based) หรือ -1 ถ้าไม่พบ
   */
  dateToMarkerDay(dateStr) {
    const index = this.dateToIndex(dateStr);
    return index >= 0 ? index : -1;
  }

  /**
   * คำนวณวันที่จากตำแหน่งในไทม์ไลน์
   * @param {number} position - ตำแหน่งในพิกเซล
   * @param {number} itemWidth - ความกว้างของแต่ละวันในพิกเซล
   * @returns {string|null} - วันที่ในรูปแบบ YYYY-MM-DD หรือ null ถ้าไม่ถูกต้อง
   */
  positionToDate(position, itemWidth) {
    if (position < 0 || !itemWidth) {
      return null;
    }

    const index = Math.floor(position / itemWidth);
    return this.indexToDate(index);
  }

  /**
   * คำนวณตำแหน่งในไทม์ไลน์จากวันที่
   * @param {string} dateStr - วันที่ในรูปแบบ YYYY-MM-DD
   * @param {number} itemWidth - ความกว้างของแต่ละวันในพิกเซล
   * @returns {number} - ตำแหน่งในพิกเซล หรือ null ถ้าไม่พบ
   */
  dateToPosition(dateStr, itemWidth) {
    if (!itemWidth) {
      return null;
    }

    const index = this.dateToIndex(dateStr);
    return index === undefined ? null : index * itemWidth;
  }

  /**
   * คำนวณตำแหน่งบน Timeline จากวันที่
   * @param {string} date - วันที่ต้องการคำนวณตำแหน่ง (YYYY-MM-DD)
   * @param {boolean} findClosestIfNotFound - หาวันใกล้เคียงถ้าไม่พบวันนี้
   * @returns {number} - ตำแหน่ง ใน this.dateToIndex หรือ this.dateToIndex หากไม่พบ
   */
  calculateBLockIndex(date, findClosestIfNotFound = true) {
    let index = this.dateToIndex(date);

    if (index === undefined && findClosestIfNotFound) {
      let prevDate = new Date(date);
      const firstDate = new Date(this.firstDate);
      while (index === undefined && prevDate >= firstDate) {
        prevDate.setDate(prevDate.getDate() - 1);
        index = this.dateToIndex(this.timeline.formatDate(prevDate, 'y-m-d', 0));
      }
    }

    return index === undefined ? 0 : index;
  }

  /**
   * เลื่อนวันที่ไปตาม index delta
   * @param {string} dateStr - วันที่เริ่มต้น
   * @param {number} indexDelta - จำนวน index ที่เลื่อน (+/-)
   * @returns {string|null} - วันที่ใหม่
   */
  shiftDateByIndexDelta(dateStr, indexDelta) {
    if (indexDelta === 0) return dateStr;

    const currentIndex = this.dateToIndex(dateStr);
    if (currentIndex === undefined) return null;

    const newIndex = currentIndex + indexDelta;
    return this.indexToDate(newIndex);
  }

  /**
   * คำนวณ delta ระหว่างสองวันที่ในรูปแบบ index
   * @param {string} fromDate - วันที่เริ่มต้น
   * @param {string} toDate - วันที่สิ้นสุด
   * @returns {number} - จำนวน index ที่ต่างกัน
   */
  calculateIndexDelta(fromDate, toDate) {
    const fromIndex = this.dateToIndex(fromDate);
    const toIndex = this.dateToIndex(toDate);

    if (fromIndex === undefined || toIndex === undefined) return 0;

    return toIndex - fromIndex;
  }

  /**
   * คำนวณตำแหน่งที่มองเห็นได้ของ Block
   * @param {Object} block - Block ที่ต้องการคำนวณ
   * @param {number} itemWidth - ความกว้างของแต่ละวันในพิกเซล
   * @returns {Object} - Block ที่มีตำแหน่งที่มองเห็นได้
   */
  calculateVisiblePosition(block, itemWidth) {
    if (!block) return block;

    const startIndex = this.dateToIndex(block.startDate);
    const timelineWidth = this.timeline.headers.length * itemWidth;

    block.left = startIndex * itemWidth;
    block.width = block.duration * itemWidth;

    block.visibleLeft = block.left;
    block.visibleWidth = block.width;
    block.isPartiallyVisible = false;
    block.isVisible = true;

    if (block.left < 0) {
      block.visibleWidth += block.left;
      block.visibleLeft = 0;
      block.isPartiallyVisible = true;
    }

    if (block.visibleLeft + block.visibleWidth > timelineWidth) {
      block.visibleWidth = timelineWidth - block.visibleLeft;
      block.isPartiallyVisible = true;
    }

    if (block.visibleWidth <= 0) {
      block.isVisible = false;
    }

    return block;
  }

  /**
   * สร้าง headers สำหรับ Timeline จากข้อมูลในช่วงที่กำหนด
   * @param {Date|string} startDate - วันที่เริ่มต้น
   * @param {Date|string} endDate - วันที่สิ้นสุด
   * @returns {Array<string>} - รายการวันที่สำหรับแสดงเป็น headers
   */
  buildHeaders(startDate, endDate) {
    if (!startDate || !endDate) return [];

    const startDateStr = typeof startDate === 'string' ? startDate : this.timeline.formatDate(startDate, 'y-m-d', 0);
    const endDateStr = typeof endDate === 'string' ? endDate : this.timeline.formatDate(endDate, 'y-m-d', 0);

    const headers = [];

    for (const [date, index] of this.dateToIndexMap.entries()) {
      if (date >= startDateStr && date <= endDateStr) {
        headers.push(date);
      }
    }

    return headers.sort();
  }

  /**
   * ตรวจสอบว่า WorkingDaysManager ได้ถูกเริ่มต้นแล้วหรือไม่
   * @returns {boolean} true ถ้าได้เริ่มต้นแล้ว
   */
  isInitialized() {
    return this.initialized && Object.keys(this.workingDays).length > 0;
  }
}

window.WorkingDaysManager = WorkingDaysManager;
